"use client";
import { useRef, useEffect } from "react";
import { scaleBand, scaleLinear, select, axisBottom, axisLeft } from "d3";
import styles from "./styles.module.css";
import useElementSize from "@/app/hooks/useElementSize";
import {
  formatNumberToCompactValue,
  formatNumberToAccountingValue,
  formatDate,
} from "@/app/utils/intl";
import { sanitizeProposalTitle } from "@/app/utils/helpers";
import { COLOR_PALETTE_PROPOSAL } from "@/app/utils/constants";

export default function BarChart({
  data,
  average,
  top,
  xAxisLabel,
  yAxisLabel,
}: {
  data: {
    id: string;
    title: string;
    date: number;
    value: number;
    type: string;
  }[];
  average: number;
  top: {
    title: string;
    value: number;
    type: string;
  };
  xAxisLabel: string;
  yAxisLabel: string;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(chartRef);

  useEffect(() => {
    // Check clause
    if (!width) return;
    if (!chartRef.current) return;
    if (!data?.length) return;

    const margin = { top: 0, right: 20, bottom: 20, left: 60 };
    const outerWidth = width;
    const outerHeight = 360;
    const innerWidth = outerWidth - margin.left - margin.right;
    const innerHeight = outerHeight - margin.top - margin.bottom;
    const groups: string[] = data.map((_: any, i: number) => String(i));
    const max = Math.max(...data.map((d: any) => d.value)) * 1.2;

    // X scale (time)
    const xScale = scaleBand()
      .range([0, innerWidth])
      .domain(groups)
      .paddingInner(0.2);

    // Y scale
    const yScale = scaleLinear()
      .range([innerHeight, 0])
      .domain([0, max])
      .nice();

    // Render the chart
    const svg = select(svgRef.current)
      .data([null])
      .join("svg")
      .classed("svg_chart", true)
      .attr("width", outerWidth)
      .attr("height", outerHeight)
      .attr("viewBox", [0, 0, outerWidth, outerHeight]);

    // Group
    const g = svg
      .selectAll(".svg_g")
      .data([null])
      .join("g")
      .classed("svg_g", true)
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // X axis
    const xAxis = axisBottom(xScale) as any;
    svg
      .selectAll(".svg_xAxis-values")
      .data([null])
      .join("g")
      .classed("svg_xAxis-values", true)
      .attr(
        "transform",
        `translate(${margin.left}, ${outerHeight - margin.bottom})`,
      )
      .call(xAxis.tickFormat(""));

    // Y axis
    const yAxis = axisLeft(yScale)
      .ticks(5)
      .tickFormat((d: any) => formatNumberToCompactValue(d)) as any;
    svg
      .selectAll(".svg_yAxis-values")
      .data([null])
      .join("g")
      .classed("svg_yAxis-values", true)
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .call(yAxis);

    // Remove axis domain
    svg.selectAll(".domain").remove();

    // Remove axis ticks
    svg.selectAll(".tick line").remove();

    // Y grid
    svg
      .selectAll(".grid")
      .data(yScale.ticks(5))
      .join("line")
      .classed("grid", true)
      .attr("x1", margin.left)
      .attr("x2", innerWidth + margin.left)
      .attr("y1", (d: any) => yScale(d) + margin.top)
      .attr("y2", (d: any) => yScale(d) + margin.top)
      .style("stroke", "#5a728240")
      .style("stroke-width", 0.5)
      .style("stroke-dasharray", "4,4");

    // Create tooltip
    const tooltip = select(tooltipRef.current);

    // Add x-axis label
    svg.selectAll(".xAxisLabel").remove();
    svg
      .append("text")
      .classed("xAxisLabel", true)
      .attr("x", outerWidth / 2)
      .attr("y", outerHeight - margin.bottom / 2 + 6)
      .attr("text-anchor", "middle")
      .style("font-size", 12)
      .style("fill", "#5a7282")
      .text(xAxisLabel);

    // Add y-axis label
    svg.selectAll(".yAxisLabel").remove();
    svg
      .append("text")
      .classed("yAxisLabel", true)
      .attr("transform", `rotate(-90)`)
      .attr("x", -outerHeight / 2)
      .attr("y", margin.left / 2 - 20)
      .attr("text-anchor", "middle")
      .style("font-size", 12)
      .style("fill", "#5a7282")
      .text(yAxisLabel);

    // Highlight
    g.selectAll(".g_highlight")
      .data(data)
      .join("rect")
      .classed("g_highlight", true)
      .attr("id", (_: any, i: number) => `g_highlight-${i}`)
      .attr("rx", 2)
      .attr("x", (_: any, i: number) => xScale(String(i)) || 0)
      .attr("y", yScale(max))
      .attr("height", innerHeight - yScale(max))
      .attr("width", xScale.bandwidth())
      .style("fill", "transparent");

    // Bars
    g.selectAll(".g_bar")
      .data(data)
      .join("rect")
      .classed("g_bar", true)
      .attr("id", (_: any, i: number) => `g_bar-${i}`)
      .attr("rx", 2)
      .attr("x", (_: any, i: number) => xScale(String(i)) || 0)
      .attr("y", (d: any) => yScale(d.value))
      .attr("height", (d: any) => innerHeight - yScale(d.value))
      .attr("width", xScale.bandwidth())
      .style("fill", (d: any) => `${COLOR_PALETTE_PROPOSAL[d.type]}80`)
      .style("stroke", (d: any) => COLOR_PALETTE_PROPOSAL[d.type]);

    // Top line
    g.selectAll(".g_top")
      .data([null])
      .join("line")
      .classed("g_top", true)
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", yScale(top.value))
      .attr("y2", yScale(top.value))
      .style("stroke", "green")
      .style("stroke-width", 1)
      .style("stroke-dasharray", "4,4");

    // Top label
    if (top) {
      g.append("text")
        .classed("g_top-label", true)
        .attr("x", innerWidth)
        .attr("y", yScale(top.value) - 5)
        .attr("text-anchor", "end")
        .style("font-size", 12)
        .style("fill", "green")
        .text(`Max: ${formatNumberToAccountingValue(top.value)}`);
    }

    // Average line
    g.selectAll(".g_average")
      .data([null])
      .join("line")
      .classed("g_average", true)
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", yScale(average))
      .attr("y2", yScale(average))
      .style("stroke", "blue")
      .style("stroke-width", 1)
      .style("stroke-dasharray", "4,4");

    // Average label
    if (average) {
      g.append("text")
        .classed("g_average-label", true)
        .attr("x", innerWidth)
        .attr("y", yScale(average) - 5)
        .attr("text-anchor", "end")
        .style("font-size", "0.8rem")
        .style("fill", "blue")
        .text(`Avg: ${formatNumberToAccountingValue(average)}`);
    }

    // Overlay
    g.selectAll(".g_overlay")
      .data(data)
      .join("rect")
      .classed("g_overlay", true)
      .attr("data-index", (_: any, i: number) => i)
      .attr("rx", 2)
      .attr("x", (_: any, i: number) => xScale(String(i)) || 0)
      .attr("y", yScale(max))
      .attr("height", innerHeight - yScale(max))
      .attr("width", xScale.bandwidth())
      .style("fill", "transparent")
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d: any) {
        const timeZone =
          Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        const i = Number(select(this).attr("data-index"));
        svg
          .select(`#g_bar-${i}`)
          .style("stroke", "var(--blue)")
          .style("fill", "#141a9940");
        svg.select(`#g_highlight-${i}`).style("fill", "#eeeeee60");

        // Add tooltip
        tooltip.style("display", "block").html(
          `<div>
              <span>${yAxisLabel}: <strong>${formatNumberToAccountingValue(data[i].value)}</strong></span>
              <span>Proposal: <strong>${sanitizeProposalTitle(data[i].title)}</strong></span>
              <span>Created: <strong>${formatDate(String(new Date(data[i].date)), timeZone)}</strong></span>
          </div>`,
        );
        const posX = event.offsetX;
        const posY = event.offsetY - 16;
        const moveX =
          posX < innerWidth * 0.2
            ? "0%"
            : posX > innerWidth * 0.8
              ? "-100%"
              : "-50%";
        const moveY = "-100%";

        // Position tooltip
        tooltip
          .style("transform", `translate(${moveX},${moveY})`)
          .style("top", `${posY}px`)
          .style("left", `${posX}px`);
      })
      .on("mousemove", function (event) {
        const posX = event.offsetX;
        const posY = event.offsetY - 16;
        const moveX =
          posX < innerWidth * 0.2
            ? "0%"
            : posX > innerWidth * 0.8
              ? "-100%"
              : "-50%";
        const moveY = "-100%";

        // Position tooltip
        tooltip
          .style("transform", `translate(${moveX},${moveY})`)
          .style("top", `${posY}px`)
          .style("left", `${posX}px`);
      })
      .on("mouseleave", function () {
        const i = Number(select(this).attr("data-index"));
        const type = data[i]?.type;
        svg
          .select(`#g_bar-${i}`)
          .style("fill", `${COLOR_PALETTE_PROPOSAL[type]}80`)
          .style("stroke", COLOR_PALETTE_PROPOSAL[type]);
        svg.select(`#g_highlight-${i}`).style("fill", "transparent");

        // Remove tooltip
        tooltip
          .style("display", "none")
          .style("transform", "translate(0,0)")
          .style("top", 0)
          .style("left", 0)
          .html("");
      })
      .on("click", function () {
        const i = Number(select(this).attr("data-index"));
        const id = data[i]?.id;
        if (!id) return;

        // Redirect to proposal page
        const href = `/proposal/${id}`;
        window.location.href = href;
      });

    // Clean up
    return () => {
      svg.selectAll("*").remove();
    };
  }, [width, data, average, top, xAxisLabel, yAxisLabel]);

  return (
    <div className={styles.barChart} ref={chartRef}>
      <svg ref={svgRef}></svg>
      <div className={styles.barChartTooltip} ref={tooltipRef}></div>
    </div>
  );
}
